import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

const Sheet = DialogPrimitive.Root
const SheetClose = DialogPrimitive.Close
const SheetPortal = DialogPrimitive.Portal

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function SheetOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-50 bg-black/35 transition-opacity data-[state=closed]:opacity-0 data-[state=open]:opacity-100',
        className
      )}
      {...props}
    />
  )
})

const sheetVariants = cva(
  'fixed z-50 bg-white shadow-xl transition-transform data-[state=closed]:duration-200 data-[state=open]:duration-300 dark:bg-zinc-950',
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0 border-b border-zinc-200 data-[state=closed]:-translate-y-full data-[state=open]:translate-y-0 dark:border-zinc-800',
        bottom:
          'inset-x-0 bottom-0 border-t border-zinc-200 data-[state=closed]:translate-y-full data-[state=open]:translate-y-0 dark:border-zinc-800',
        left:
          'inset-y-0 left-0 h-full w-full max-w-sm border-r border-zinc-200 data-[state=closed]:-translate-x-full data-[state=open]:translate-x-0 dark:border-zinc-800',
        right:
          'inset-y-0 right-0 h-full w-full max-w-sm border-l border-zinc-200 data-[state=closed]:translate-x-full data-[state=open]:translate-x-0 dark:border-zinc-800',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  }
)

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof sheetVariants> {}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(function SheetContent({ side = 'right', className, children, ...props }, ref) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <DialogPrimitive.Content ref={ref} className={cn(sheetVariants({ side }), className)} {...props}>
        {children}
      </DialogPrimitive.Content>
    </SheetPortal>
  )
})

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function SheetTitle({ className, ...props }, ref) {
  return <DialogPrimitive.Title ref={ref} className={cn('text-lg font-semibold text-zinc-950 dark:text-zinc-50', className)} {...props} />
})

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
}
